// src/pages/AdminDashboard.js
import React, { useMemo, useState, useRef } from "react";
import { Container, Row, Col, Nav } from "react-bootstrap";
import CommunityPanel from "./CommunityPanel";   // <-- NEW: inner tabs container
import AdminServices from "./AdminServices";
import AdminJobs from "./AdminJobs";
import AdminBookings from "./AdminBookings";
import AdminEmployees from "./Employees/AdminEmployees";

/**
 * Responsive Admin Dashboard
 * - Desktop/Tablet (>= md): left sidebar tabs
 * - Mobile (< md): sticky top tabs with icons + swipeable content area
 */
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("community"); // default: Community

  // Only 3 top-level tabs now
  const tabs = useMemo(
    () => [
      { key: "community", label: "Community", icon: "bi-buildings" },
      { key: "services", label: "Services", icon: "bi-tools" },
      { key: "employee", label: "Employees", icon: "bi-people" },
      { key: "bookings", label: "Bookings", icon: "bi-people" },

    ],
    []
  );

  const renderContent = () => {
    switch (activeTab) {
      case "community":
        return <CommunityPanel />;      // <-- inner tabs: Add/View/Requests
      case "services":
        return <AdminServices />;
      case "employee":
        // return <AdminJobs />;
        return <AdminEmployees />
      case "bookings":
        return <AdminBookings />;
      default:
        return <CommunityPanel />;
    }
  };

  /* ----------------------------------------------------------
   * Mobile swipe handling (left/right) on the content area
   * ----------------------------------------------------------
   * - Threshold: 50px horizontal, minimal vertical slop (<= 30px)
   * - Ignores swipes that start on interactive controls
   * ---------------------------------------------------------- */
  const touchStartRef = useRef({ x: 0, y: 0, t: 0 });
  const touchLastRef = useRef({ x: 0, y: 0 });
  const interactiveSelector = "input, textarea, select, button, a, .btn, [role='button']";

  const handleTouchStart = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    // Ignore if started on interactive elements
    if (e.target?.closest?.(interactiveSelector)) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
    touchLastRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    touchLastRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = () => {
    const { x: x0, y: y0 } = touchStartRef.current;
    const { x: x1, y: y1 } = touchLastRef.current;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const H_THRESHOLD = 50; // px
    const V_SLOP = 30;      // px

    if (absDx >= H_THRESHOLD && absDy <= V_SLOP) {
      const currentIdx = tabs.findIndex((t) => t.key === activeTab);
      if (currentIdx === -1) return;

      if (dx < 0) {
        // Left swipe -> next tab
        const nextIdx = Math.min(currentIdx + 1, tabs.length - 1);
        setActiveTab(tabs[nextIdx].key);
      } else if (dx > 0) {
        // Right swipe -> previous tab
        const prevIdx = Math.max(currentIdx - 1, 0);
        setActiveTab(tabs[prevIdx].key);
      }
      if (navigator.vibrate) navigator.vibrate(10); // optional haptic
    }
  };

  return (
    <Container fluid className="mt-3">
      <Row className="g-0">
        {/* -------- Mobile top tabs (< md) -------- */}
        <Col xs={12} className="d-block d-md-none">
          <div
            className="bg-light border-bottom"
            style={{ position: "sticky", top: 0, zIndex: 20 }}
          >
            {/* Header row */}
            <div className="px-3 py-2 d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Admin Panel</h5>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => alert("Logging out...")}
              >
                Logout
              </button>
            </div>

            {/* Mobile tabs bar with icons */}
            <Nav
              variant="pills"
              className="px-2 pb-2 flex-nowrap"
              style={{ overflowX: "auto", gap: "0.5rem" }}
            >
              {tabs.map((t) => (
                <Nav.Item key={t.key}>
                  <Nav.Link
                    onClick={() => setActiveTab(t.key)}
                    active={activeTab === t.key}
                    className="text-nowrap d-flex align-items-center gap-2"
                  >
                    <i className={`bi ${t.icon}`} aria-hidden="true" />
                    <span>{t.label}</span>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </div>
        </Col>

        {/* -------- Desktop/Tablet Sidebar (>= md) -------- */}
        <Col
          md={3}
          className="d-none d-md-block bg-light border-end"
          style={{ minHeight: "100vh" }}
        >
          <div className="p-3">
            <h4>Admin Panel</h4>
          </div>
          <Nav className="flex-column px-2">
            {tabs.map((t) => (
              <Nav.Link
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                active={activeTab === t.key}
                className="py-2 d-flex align-items-center gap-2"
              >
                <i className={`bi ${t.icon}`} aria-hidden="true" />
                <span>{t.label}</span>
              </Nav.Link>
            ))}
            <Nav.Link onClick={() => alert("Logging out...")} className="mt-2">
              Logout
            </Nav.Link>
          </Nav>
        </Col>

        {/* -------- Content Area -------- */}
        <Col
          xs={12}
          md={9}
          className="p-3"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {renderContent()}
        </Col>
      </Row>
    </Container>
  );
}
